import { ValidationControlBase } from "@frui.ts/bootstrap";
import { bound } from "@frui.ts/helpers";
import { IBindingProps } from "@frui.ts/views";
import DatePicker from "@edaz/edazkit/DatePicker";
import { action, observable } from "mobx";
import format from "date-fns-tz/format";
import React from "react";
import { Form } from "react-bootstrap";
import { START_OF_SALE } from "../../config";
import { Modifier } from "react-day-picker";
import { setVisitedProperty } from "utils/validationPatch";
import { isDatepicker } from "./utils";
import { utcToCZZonedTime, zonedCZTimeToUtc } from "../../utils/dateUtils";
import { startOfDay } from "date-fns";

export interface DatePickerBoxProps {
  ignoreTime?: boolean;
  id?: string;
  datePickerLocalization: any;
  errorLocalization: (code: string) => string;
  errorMessage?: string;
  value?: Date;
  fromMonth?: Date;
  toMonth?: Date;
  month?: Date;
  today?: Date;
  forSale?: boolean;
  disabledDays?: Modifier | Modifier[];
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
  useEndBoundaryAsDefault?: boolean;
  autoComplete?: "off" | "on";
}

export default class DatePickerBox<TTarget, TProperty> extends ValidationControlBase<
  TTarget,
  DatePickerBoxProps & IBindingProps<TTarget>
> {
  private dateRegex = /%date%/g;
  @observable closePicker = false;
  @observable inputValue?: string;

  @bound
  protected handleValueChanged(formattedDate: string, date?: Date, inputValue?: string) {
    this.inputValue = inputValue;
    // Select date in czech midnight or current time for czech current day
    let czechDate;
    if (date) {
      const czechToday = utcToCZZonedTime(this.props.today ?? new Date());
      const dateFormat = "yyyy-MM-dd";
      if (format(date, dateFormat) === format(czechToday, dateFormat)) {
        czechDate = this.props.today ?? new Date();
      } else if (!this.props.ignoreTime) {
        czechDate = zonedCZTimeToUtc(format(date, dateFormat));
      } else {
        date.setHours(0, 0, 0, 0);
        czechDate = date;
      }
    }

    this.setValue(czechDate);
  }

  @action
  formatValue = (date: any) => {
    let newDate = null;
    if (typeof date === "string") {
      newDate = new Date(date);
    }
    if (date instanceof Date) {
      newDate = date;
    }
    if (newDate instanceof Date) {
      return utcToCZZonedTime(newDate);
    } else {
      return date;
    }
  };

  @bound
  protected onBlur(e: React.FocusEvent<HTMLInputElement>) {
    const { target, property } = this.props as { target: TTarget; property: keyof TTarget & string };

    if (this.props.onBlur) {
      this.props.onBlur(e);
    }

    const inputElement = e.relatedTarget as HTMLInputElement | null;
    if (!inputElement || !isDatepicker(inputElement)) {
      setVisitedProperty(target, property);
    }
  }

  @bound
  protected onDayPickerHide() {
    const { target, property } = this.props as { target: TTarget; property: keyof TTarget & string };
    setVisitedProperty(target, property);
  }

  @bound
  protected renderInner() {
    let validationError = this.getValidationError() || this.props.errorMessage;
    const { useEndBoundaryAsDefault, errorLocalization, forSale, datePickerLocalization, placeholder, id, today } = this.props;

    const date = this.formatValue(this.value);
    const isBeforeSale = forSale && date && START_OF_SALE && new Date(START_OF_SALE) > new Date(date);
    if (isBeforeSale) {
      validationError = "min";
    }

    const hasInvalidInputValue = this.inputValue && !this.value && validationError;
    if (hasInvalidInputValue) {
      validationError = "invalidDateFormat";
    }
    return (
      <>
        <DatePicker
          localization={datePickerLocalization}
          {...this.props}
          onChange={this.handleValueChanged}
          value={this.formatValue(this.value as TProperty)}
          hideOnDayClick={true}
          onDayPickerHide={this.onDayPickerHide}
          placeholder={placeholder}
          isInvalid={!!validationError}
          useEndBoundaryAsDefault={useEndBoundaryAsDefault}
          today={startOfDay(today ?? new Date())}
          inputProps={{
            id,
            "data-testid": id,
            onBlur: this.onBlur,
            autoComplete: this.props.autoComplete,
          }}
        />
        {validationError && (
          <Form.Control.Feedback type="invalid" className="pre-line">
            {errorLocalization(validationError)}
          </Form.Control.Feedback>
        )}
      </>
    );
  }
}
